/**
 * FollowUpBox
 * ------------------------------------------------------------------
 * A discrete "↩ Question de suivi" pill that expands into a small
 * textarea + submit. Sends {summary, question, model} to
 * /api/summarize/followup, then displays the answer inline.
 *
 * This surfaces the workflow prescribed by the book's
 * "Pour aller plus loin" sections without forcing the user to
 * re-paste the summary as a new source (and without consuming
 * another entry from their 10-summary free quota).
 */
import { useL } from "@/lib/useL";
import { QcmTools } from "@/components/QcmTools";
import React, { useState, useEffect } from "react";
import { ACTION_HELP } from "@/components/actionHelp";
import { CornerDownLeft, Loader2, Copy, Check, Plus, X, ListChecks, Mail, Accessibility, CalendarDays, PencilRuler, MessageSquareQuote } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { renderSummary } from "@/lib/renderSummary";
import { InfoTip } from "@/components/InfoTip";
import { PaidOnlyNote } from "@/components/PaidOnlyNote";
import { useBilling } from "@/contexts/BillingContext";
import { ActionBarButton, ActionBarHeader, optionCls } from "@/components/ActionBar";

// Discovery: pulse the "Question de suivi" open button until the user has
// clicked it at least once on this device. Flag mirrored in localStorage.
const LS_FU_USED = "sux1_fu_used";
const readFuUsed = () => {
  try { return localStorage.getItem(LS_FU_USED) === "1"; } catch { return false; }
};
const markFuUsed = () => {
  try { localStorage.setItem(LS_FU_USED, "1"); } catch {}
};

// Pre-made quick actions surfaced as pill buttons under the textarea.
// Click = fill the question field with a battle-tested teacher prompt.
const QUICK_ACTIONS = [
  {
    id: "qcm",
    desc: "Un quiz de 5 questions à choix multiples (A/B/C/D) avec niveau de difficulté et corrigé justifié. Idéal pour vérifier les acquis en fin de cours.",
    label: "5 QCM",
    icon: ListChecks,
    prompt:
      "Génère un quiz de 5 QCM à partir de ce résumé. Pour chaque question : 4 propositions (A/B/C/D), une seule bonne réponse, et le niveau de difficulté (facile/moyen/difficile). Donne le corrigé en fin de quiz avec une courte justification.",
  },
  {
    id: "mail",
    desc: "Un mail bienveillant de 150 mots maximum aux parents : ce que l'enfant apprend, pourquoi c'est important, comment l'aider à la maison.",
    label: "Mail parents",
    icon: Mail,
    prompt:
      "Rédige un mail diplomate aux parents expliquant le contenu de ce chapitre, pourquoi il est important pour leur enfant, et quels appuis ils peuvent apporter à la maison. Ton bienveillant, 150 mots maximum.",
  },
  {
    id: "dys",
    desc: "Le résumé réécrit pour un élève DYS : phrases de 12 mots maximum, une idée par ligne, vocabulaire simple, mots-clés en gras.",
    label: "Reformuler DYS",
    icon: Accessibility,
    prompt:
      "Reformule ce résumé pour un élève DYS : phrases courtes (12 mots max), vocabulaire simple, une idée par ligne, pas de double négation, mots-clés en gras.",
  },
  {
    id: "seance",
    desc: "Un plan de séance de 55 minutes : objectifs, matériel, déroulé minuté (accroche, cours, activité, correction, trace écrite), différenciation et évaluation.",
    label: "Plan de séance",
    icon: CalendarDays,
    prompt:
      "Transforme ce résumé en plan de séance de 55 minutes avec : objectifs d'apprentissage, matériel, déroulé minuté (accroche, cours, activité, correction, trace écrite), différenciation, évaluation.",
  },
  {
    id: "correction",
    desc: "Un exercice d'application avec sa correction type détaillée et un barème sur 20 aux critères explicites.",
    label: "Correction type",
    icon: PencilRuler,
    prompt:
      "À partir de ce résumé, propose un exercice d'application pour les élèves et sa correction-type détaillée. Barème sur 20 avec critères explicites. Précise le niveau visé.",
  },
  {
    id: "debat",
    desc: "3 sujets de débat ou de dissertation, chacun avec une problématique, 3 arguments pour, 3 contre et une référence culturelle.",
    label: "3 idées débat",
    icon: MessageSquareQuote,
    prompt:
      "Propose 3 sujets de débat ou de dissertation issus de ce contenu. Pour chaque sujet : une problématique claire, 3 arguments pour, 3 arguments contre, et une référence culturelle ou historique pour nourrir la réflexion.",
  },
];

const QUICK_EN = {
  qcm: ["5 MCQs", "A 5-question multiple-choice quiz (A/B/C/D) with difficulty level and justified answers. Ideal to check learning at the end of a lesson.", "Generate a 5-question multiple-choice quiz from this summary. For each question: 4 options (A/B/C/D), one correct answer, and the difficulty level (easy/medium/hard). Give the answer key at the end with a short justification."],
  mail: ["Email to parents", "A kind email of 150 words maximum to parents: what the child is learning, why it matters, how to help at home.", "Write a tactful email to parents explaining the content of this chapter, why it matters for their child, and how they can help at home. Kind tone, 150 words maximum."],
  dys: ["DYS rephrasing", "The summary rewritten for a DYS pupil: sentences of 12 words maximum, one idea per line, simple vocabulary, keywords in bold.", "Rephrase this summary for a DYS pupil: short sentences (12 words max), simple vocabulary, one idea per line, no double negatives, keywords in bold."],
  seance: ["Lesson plan", "A 55-minute lesson plan: objectives, materials, timed sequence (hook, lesson, activity, correction, written record), differentiation and assessment.", "Turn this summary into a 55-minute lesson plan with: learning objectives, materials, timed sequence (hook, lesson, activity, correction, written record), differentiation, assessment."],
  correction: ["Model answer", "A practice exercise with a detailed model answer and a marking scheme out of 20 with explicit criteria.", "From this summary, propose a practice exercise for pupils and its detailed model answer. Marking scheme out of 20 with explicit criteria. State the target level."],
  debat: ["3 debate ideas", "3 debate or essay topics, each with a key question, 3 arguments for, 3 against and a cultural reference.", "Propose 3 debate or essay topics from this content. For each: a clear key question, 3 arguments for, 3 arguments against, and a cultural or historical reference to fuel reflection."],
};
const QUICK_ACTIONS_EN = QUICK_ACTIONS.map((a) => ({ ...a, label: QUICK_EN[a.id][0], desc: QUICK_EN[a.id][1], prompt: QUICK_EN[a.id][2] }));

export function FollowUpBox({ summary, model, initialQuestion, initialQuestionLabel, onPrefillConsumed, summaryId, onAppended, title }) {
  const L = useL();
  const quick = L(QUICK_ACTIONS, QUICK_ACTIONS_EN);
  // Auto-open the panel when we land here with a pre-filled question so
  // the teacher sees the prompt waiting for them (no extra click).
  const [open, setOpen] = useState(Boolean(initialQuestion));
  const [question, setQuestion] = useState(initialQuestion || "");
  const [answer, setAnswer] = useState("");
  const [askedQuestion, setAskedQuestion] = useState("");
  const [appendState, setAppendState] = useState("idle"); // idle | busy | done

  const undoAppend = async () => {
    setAppendState("busy");
    try {
      const { data } = await api.post(`/summaries/${summaryId}/remove-complement`, { index: -1 });
      setAppendState("idle");
      if (onAppended) onAppended(data.summary);
      toast.success(L("Ajout annulé : le complément a été retiré du résumé.", "Undone: the addition was removed from the summary."));
    } catch {
      setAppendState("done");
      toast.error(L("Impossible d'annuler l'ajout.", "Could not undo."));
    }
  };

  const appendToSummary = async () => {
    if (!summaryId || appendState !== "idle") return;
    setAppendState("busy");
    try {
      const { data } = await api.post(`/summaries/${summaryId}/append`, { question: askedQuestion, answer });
      setAppendState("done");
      if (onAppended) onAppended(data.summary);
      toast.success(L("Réponse ajoutée à la fin du résumé : elle sera incluse dans le partage, le PDF et l'impression.", "Answer added at the end of the summary: it will be included in sharing, the PDF and printing."));
    } catch {
      setAppendState("idle");
      toast.error(L("Impossible d'ajouter la réponse au résumé.", "Could not add the answer to the summary."));
    }
  };
  const [busy, setBusy] = useState(false);
  // Pulse-ring discoverability until the first open-click.
  const [neverUsed, setNeverUsed] = useState(() => !readFuUsed());

  const openPanel = () => {
    setOpen(true);
    if (neverUsed) {
      markFuUsed();
      setNeverUsed(false);
    }
  };

  // If a new prefill arrives after mount (e.g. the user ran a second
  // summary with another cas pratique selected), hydrate the textarea.
  useEffect(() => {
    if (initialQuestion && initialQuestion !== question) {
      setQuestion(initialQuestion);
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuestion]);

  const { canUseExtras } = useBilling();
  const submit = async () => {
    const q = question.trim();
    if (q.length < 3) {
      toast.error("Question trop courte");
      return;
    }
    setBusy(true);
    setAnswer("");
    setAppendState("idle");
    setAskedQuestion(q);
    try {
      const res = await api.post("/summarize/followup", {
        summary,
        question: q,
        model,
      });
      setAnswer(res.data?.answer || "");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message ||
            L("Une erreur est survenue. Réessayez dans un instant.", "Something went wrong. Please try again in a moment.");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    setQuestion("");
    setAnswer("");
    if (onPrefillConsumed) onPrefillConsumed();
  };

  if (!open) {
    return (
      <div className="mt-8 max-w-3xl">
        <ActionBarButton
          icon={CornerDownLeft}
          label={L("Question de suivi", "Follow-up question")}
          onClick={openPanel}
          testid="followup-open-btn"
          className="sm:min-h-[4.5rem]"
          help={ACTION_HELP.followUp}
          pulse={neverUsed}
          badge={neverUsed ? L("Nouveau", "New") : null}
          tip={L("Question de suivi : posez une question complémentaire sur ce résumé (5 QCM, mail aux parents, version DYS…), 1 crédit les 5 questions.", "Follow-up question: ask a further question about this summary (5 MCQs, email to parents, DYS version…), 1 credit for 5 questions.")}
        />
        <p className="mt-2 text-center text-[11px] text-muted-foreground">
          {L(<>Posez une question complémentaire, ou en 1 clic : <b>générer un quiz
          de 5 QCM avec corrigé</b>, préparer un plan de séance, un mail aux
          parents, une version DYS… pour seulement 1 crédit les 5 questions.</>, <>Ask a further question, or in 1 click: <b>generate a 5-question MCQ quiz with answers</b>, a lesson plan, an email to parents, a DYS version… for just 1 credit per 5 questions.</>)}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 max-w-3xl sm:col-span-2 border border-border bg-card" data-testid="followup-panel">
      <ActionBarHeader
        icon={CornerDownLeft}
        label={initialQuestionLabel ? L(`Prompt du ${initialQuestionLabel} prêt`, `${initialQuestionLabel} prompt ready`) : L("Question de suivi", "Follow-up question")}
        onCollapse={close}
        testid="followup-close-btn"
        extra={
<InfoTip light testid="followup-header-info" content={ACTION_HELP.followUp} />
        }
      />
      <div className="p-4">
        {!canUseExtras && <PaidOnlyNote testid="followup-paid-note" />}
      <textarea
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder={L("Ex : « Génère 5 QCM avec corrigé », « Rédige un mail aux parents », « Reformule pour un élève DYS »…", "E.g. « Generate 5 MCQs with answers », « Write an email to parents », « Rephrase for a DYS pupil »…")}
        rows={3}
        data-testid="followup-question-input"
        className="w-full bg-background border border-border p-3 font-sans text-sm resize-y focus:outline-none focus:border-[#002FA7]"
        disabled={busy}
      />

      {/* Quick-action pills: 1 click = ready-to-use teacher prompt */}
      <div className="mt-3">
        <div className="text-center text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground mb-2">
          {L("Idées rapides : 1 clic pour pré-remplir", "Quick ideas: 1 click to pre-fill")}
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {quick.map((action) => {
            const Icon = action.icon;
            return (
              <span key={action.id} className="inline-flex items-center">
              <button
                type="button"
                onClick={() => setQuestion(action.prompt)}
                data-testid={`followup-quick-${action.id}`}
                disabled={busy}
                className={optionCls(question === action.prompt)}
                data-tip={`${action.label} : ${action.prompt}`}
              >
                <Icon className="w-3 h-3" />
                {action.label}
              </button>
              <InfoTip testid={`followup-quick-${action.id}-info`} content={<><b>{action.label}</b> : {action.desc}</>} />
              </span>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={submit}
          disabled={busy || !canUseExtras || question.trim().length < 3}
          data-testid="followup-submit-btn"
          data-tip={L("Envoyer la question : la réponse s'appuie uniquement sur le résumé ci-dessus.", "Send the question: the answer relies only on the summary above.")}
          className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#002FA7] transition-colors"
        >
          {busy ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {L("Réflexion…", "Thinking…")}
            </>
          ) : (
            <>
              <CornerDownLeft className="w-3.5 h-3.5" />
              {L("Envoyer", "Send")}
            </>
          )}
        </button>
        <span className="text-[11px] text-muted-foreground">
          {L("Basé uniquement sur le résumé ci-dessus, pas de nouveau document.", "Based only on the summary above, no new document.")}
        </span>
      </div>

      {answer && (
        <div className="mt-5 border-t border-border pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="sux-marker">{L("Réponse", "Answer")}</div>
            <button
              type="button"
              onClick={() =>
                navigator.clipboard.writeText(answer).then(
                  () => toast.success(L("Copié", "Copied")),
                  () => toast.error(L("Impossible de copier", "Could not copy"))
                )
              }
              data-testid="followup-copy-btn"
              className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
            >
              <Copy className="w-3 h-3" /> {L("Copier", "Copy")}
            </button>
          </div>
          <article
            className="summary-content max-w-none notranslate"
            translate="no"
            dangerouslySetInnerHTML={{ __html: renderSummary(answer) }}
            data-testid="followup-answer"
          />
          <div className="mt-4"><QcmTools text={answer} title={title} summaryId={summaryId} testid="followup-qcm" /></div>
          {summaryId && (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={appendToSummary}
                disabled={appendState !== "idle"}
                data-testid="followup-append-btn"
                data-tip={L("Ajoute cette réponse à la fin du résumé, sous le titre « Complément : votre question ». Elle sera incluse dans le partage, le lien public, le PDF et l'impression.", "Adds this answer at the end of the summary, under the heading « Complément: your question ». It will be included in sharing, the public link, the PDF and printing.")}
                className={`inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2.5 border transition-colors ${
                  appendState === "done"
                    ? "bg-[#002FA7] text-white border-[#002FA7]"
                    : "border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white"
                } disabled:cursor-default`}
              >
                {appendState === "busy" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : appendState === "done" ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                {appendState === "done" ? L("Ajouté au résumé", "Added to summary") : L("Ajouter au résumé", "Add to summary")}
              </button>
              {appendState === "done" ? (
                <button
                  type="button"
                  onClick={undoAppend}
                  data-testid="followup-undo-append-btn"
                  data-tip={L("Retire ce complément de la fin du résumé.", "Removes this addition from the end of the summary.")}
                  className="inline-flex items-center gap-1.5 font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 border border-border hover:border-[#FF2A00] hover:text-[#FF2A00] transition-colors"
                >
                  <X className="w-3.5 h-3.5" /> {L("Annuler l'ajout", "Undo")}
                </button>
              ) : (
                <span className="text-[11px] text-muted-foreground">
                  {L("Pour l'inclure dans le partage, le PDF et l'impression.", "To include it in sharing, the PDF and printing.")}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
